<template>
  <el-drawer title="作品评论" :visible="visible" size="68%" :before-close="beforeClose">
    <div class="body" v-loading="loading" element-loading-text="正在加载评论">
      <el-descriptions v-if="item" :column="1" border size="small">
        <el-descriptions-item label="作品">{{ item.title || item.source_id }}</el-descriptions-item>
        <el-descriptions-item label="平台">{{ platformName(item.platform) }}</el-descriptions-item>
      </el-descriptions>
      <div class="toolbar">
        <span class="summary">
          <template v-if="platformCommentCount !== null">平台评论数 {{ number(platformCommentCount) }}；</template>
          已采集 {{ number(total) }} 条（一级 {{ number(topLevelCount) }}，回复 {{ number(replyCount) }}）
        </span>
        <div>
          <el-button
            type="danger"
            plain
            size="small"
            :loading="clearing"
            :disabled="loading || refreshing || deletingCommentId !== '' || total === 0"
            @click="clearComments"
          >清空评论</el-button>
          <el-button type="primary" plain size="small" :loading="refreshing" :disabled="busy" @click="refresh">
            {{ refreshing ? '评论采集中…' : '采集作品评论' }}
          </el-button>
        </div>
      </div>
      <el-alert
        v-if="platformCommentCount !== null && total < platformCommentCount"
        class="collection-warning"
        type="warning"
        :closable="false"
        show-icon
        :title="`平台显示 ${number(platformCommentCount)} 条评论，当前已采集 ${number(total)} 条明细`"
        description="评论明细尚未完整采集，或平台存在已删除、折叠、审核隐藏而不返回的评论。"
      />
      <el-table :data="comments" empty-text="暂未采集到评论，可点击“采集作品评论”开始采集" class="comments-table">
        <el-table-column label="类型" width="80">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.parent_comment_id ? 'info' : 'success'">
              {{ scope.row.parent_comment_id ? '回复' : '一级' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="评论人" width="140">
          <template slot-scope="scope">{{ scope.row.author_name || scope.row.author_id || '-' }}</template>
        </el-table-column>
        <el-table-column label="评论内容" min-width="280">
          <template slot-scope="scope"><div class="comment-content">{{ scope.row.content }}</div></template>
        </el-table-column>
        <el-table-column label="点赞" width="80" align="right">
          <template slot-scope="scope">{{ number(scope.row.like_count) }}</template>
        </el-table-column>
        <el-table-column label="评论时间" width="165">
          <template slot-scope="scope">{{ formatDate(scope.row.published_at) }}</template>
        </el-table-column>
        <el-table-column label="首次发现" width="165">
          <template slot-scope="scope">{{ formatDate(scope.row.first_collected_at) }}</template>
        </el-table-column>
        <el-table-column label="最近确认" width="165">
          <template slot-scope="scope">{{ formatDate(scope.row.last_collected_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="70" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="text"
              class="delete-button"
              :loading="deletingCommentId === scope.row.comment_id"
              :disabled="refreshing || clearing || (deletingCommentId !== '' && deletingCommentId !== scope.row.comment_id)"
              @click="deleteComment(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="total > pageSize"
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        @current-change="changePage"
      />
    </div>
  </el-drawer>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'

export default {
  props: ['visible', 'item'],
  data () {
    return {
      loading: false,
      refreshing: false,
      clearing: false,
      deletingCommentId: '',
      comments: [],
      total: 0,
      topLevelCount: 0,
      replyCount: 0,
      page: 1,
      pageSize: 50
    }
  },
  computed: {
    busy () {
      return this.loading || this.refreshing || this.clearing || this.deletingCommentId !== ''
    },
    platformCommentCount () {
      const key = this.item && this.item.platform === 'xiaohongshu' ? 'xhs_comment_count' : 'comment_count'
      const metric = ((this.item && this.item.latest_metrics) || [])
        .find(value => value.metric_key === key && (!value.availability || value.availability === 'available'))
      if (!metric || metric.value === null || metric.value === undefined) return null
      const value = Number(metric.value)
      return Number.isFinite(value) ? value : null
    }
  },
  watch: {
    visible (value) {
      if (value && this.item) {
        this.page = 1
        this.load()
      }
    }
  },
  methods: {
    async load () {
      if (!this.item) return
      this.loading = true
      try {
        const result = await yuqingApi.get(
          `creator-content/${this.item.id}/comments?page=${this.page}&page_size=${this.pageSize}`
        )
        this.comments = result.comments || []
        this.total = Number(result.total || 0)
        this.topLevelCount = Number(result.top_level_count || 0)
        this.replyCount = Number(result.reply_count || 0)
      } catch (error) {
        this.$message.error(error.message || '评论加载失败')
      } finally {
        this.loading = false
      }
    },
    async refresh () {
      if (!this.item || this.refreshing) return
      this.refreshing = true
      try {
        const result = await yuqingApi.post(`creator-content/${this.item.id}/comments/refresh`, {})
        const runId = result.comment_collection && result.comment_collection.run_id
        if (!runId) throw new Error('评论采集任务启动失败：未返回任务编号')
        this.$message.info('评论采集任务已启动，不会影响运营数据更新')
        const run = await this.waitRun(runId)
        if (!run) {
          this.$message.info('评论仍在后台采集，可在运行记录中查看结果')
          return
        }
        if (run.status === 'completed') this.$message.success('评论采集完成')
        else if (run.status === 'partially_completed') this.$message.warning('评论部分采集完成，请查看运行记录')
        else this.$message.error('评论采集失败，系统不会自动重试，请查看运行记录')
        this.page = 1
        await this.load()
      } catch (error) {
        this.$message.error(error.message || '评论采集失败')
      } finally {
        this.refreshing = false
      }
    },
    async waitRun (runId) {
      for (let i = 0; i < 30; i++) {
        await new Promise(resolve => setTimeout(resolve, 1000))
        const result = await yuqingApi.get(`creator-content/runs/${runId}`)
        const run = result.run || {}
        if (['completed', 'partially_completed', 'failed'].includes(run.status)) return run
      }
      return null
    },
    async deleteComment (comment) {
      if (!this.item || !comment || !comment.comment_id || this.busy) return
      try {
        await this.$confirm('确认删除这条已采集评论？删除后可重新采集作品评论。', '删除评论', {
          confirmButtonText: '删除',
          cancelButtonText: '取消',
          type: 'warning'
        })
      } catch (ignored) {
        return
      }
      this.deletingCommentId = comment.comment_id
      try {
        await yuqingApi.delete(
          `creator-content/${this.item.id}/comments/${encodeURIComponent(comment.comment_id)}`
        )
        this.comments = this.comments.filter(item => item.comment_id !== comment.comment_id)
        this.total = Math.max(0, this.total - 1)
        if (comment.parent_comment_id) this.replyCount = Math.max(0, this.replyCount - 1)
        else this.topLevelCount = Math.max(0, this.topLevelCount - 1)
        if (!this.comments.length && this.page > 1) {
          this.page--
          await this.load()
        }
        this.$message.success('评论已删除')
      } catch (error) {
        this.$message.error(error.message || '评论删除失败')
      } finally {
        this.deletingCommentId = ''
      }
    },
    async clearComments () {
      if (!this.item || this.busy || this.total === 0) return
      try {
        await this.$confirm(
          `确认清空当前作品已采集的 ${this.total} 条评论？此操作不会删除平台原评论。`,
          '清空评论',
          { confirmButtonText: '清空', cancelButtonText: '取消', type: 'warning' }
        )
      } catch (ignored) {
        return
      }
      this.clearing = true
      try {
        const result = await yuqingApi.delete(`creator-content/${this.item.id}/comments`)
        this.comments = []
        this.total = 0
        this.topLevelCount = 0
        this.replyCount = 0
        this.page = 1
        this.$message.success(`已清空 ${Number(result.deleted || 0)} 条评论`)
      } catch (error) {
        this.$message.error(error.message || '评论清空失败')
      } finally {
        this.clearing = false
      }
    },
    close () {
      if (this.busy) return
      this.reset()
      this.$emit('update:visible', false)
    },
    beforeClose (done) {
      if (this.busy) {
        this.$message.info('评论正在处理，请稍候')
        return
      }
      this.reset()
      done()
      this.$emit('update:visible', false)
    },
    changePage (page) {
      this.page = page
      this.load()
    },
    reset () {
      this.comments = []
      this.total = 0
      this.topLevelCount = 0
      this.replyCount = 0
      this.page = 1
    },
    number (value) { return Number(value || 0).toLocaleString('zh-CN') },
    formatDate (value) {
      if (value === null || value === undefined || value === '') return '-'
      const text = String(value).trim()
      let date
      if (/^\d{10}$/.test(text)) date = new Date(Number(text) * 1000)
      else if (/^\d{13}$/.test(text)) date = new Date(Number(text))
      else date = new Date(text.replace(' ', 'T'))
      if (Number.isNaN(date.getTime())) return '-'
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    platformName (value) { return { douyin: '抖音', bilibili: 'B站', xiaohongshu: '小红书', wechat_channels: '视频号' }[value] || value || '-' }
  }
}
</script>

<style scoped>
.body { padding: 0 20px 20px }
.toolbar { display: flex; justify-content: space-between; align-items: center; margin: 16px 0 10px }
.summary { color: #606266; font-size: 13px }
.collection-warning { margin-bottom: 10px; }
.comments-table { width: 100% }
.pagination { margin-top: 16px; text-align: right; }
.comment-content { white-space: pre-wrap; word-break: break-word; line-height: 20px; }
.delete-button { color: #f56c6c; }
</style>
